import React, { forwardRef, useState, useEffect, useContext } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Grow,
  IconButton,
  Typography,
} from '@mui/material'
import { IntakeContext } from 'context/IntakeContext'
import { motion } from 'framer-motion'
import { Close } from '@mui/icons-material'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { callZapier } from 'utils/zapier/callZapier'
import { format, differenceInYears, addYears, addDays } from 'date-fns'

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

export default function CreateOpportunityModal({
  onClose,
  title = 'Create a Salesforce Opportunity',
  buttonText = 'Create Opportunity',
}) {
  const [isCreatingOpp, setIsCreatingOpp] = useState(false)
  const { intakeState } = useContext(IntakeContext)
  const [firstName, setFirstName] = useState(
    intakeState?.firstName || 'No First Name'
  )
  const [lastName, setLastName] = useState(
    intakeState?.lastName || 'No Last Name'
  )
  const [salesforceAccountID, setSalesforceAccountID] = useState(
    intakeState?.salesforceAccountID
  )
  const [amount, setAmount] = useState(100000)
  const [pitchType, setPitchType] = useState('')
  const [closeDate, setCloseDate] = useState(
    format(addDays(new Date(), 60), 'yyyy-MM-dd')
  )
  const [stage, setStage] = useState('')
  const [nextStep, setNextStep] = useState('')

  const handleSubmit = async () => {
    setIsCreatingOpp(true)
    const sfOpportunity = {
      salesforceAccountID: salesforceAccountID,
      opportunityName: `${firstName} ${lastName} FIA`,
      pitchType: pitchType,
      amount: amount,
      closeDate: closeDate,
      stage: stage,
      nextStep: nextStep,
    }

    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/u5vucft/',
      payload: {
        user: sfOpportunity,
      },
      notificationMessage: `You have successfully created a new Salesforce Opportunity for ${firstName} ${lastName}!`,
      sendNotification: true,
    })
    setIsCreatingOpp(false)
    onClose()
  }
  return (
    <Dialog
      open
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth={'md'}
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogContent sx={{ overflow: 'visible' }}>
        <motion.div
          key="scheduler"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <Box
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              top: -64,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 0,
                right: -16,
                color: '#fff',
                zIndex: 1,
                backgroundColor: '#000',
                '&:hover': {
                  backgroundColor: '#000',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box p={3}>
            <Box>
              <Typography variant="headerTitle" sx={{ color: '#262627' }}>
                {title}
              </Typography>
            </Box>
            <Box mt={3}>
              <Grid container columnSpacing={3} rowSpacing={2}>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'amount',
                    label: 'Amount',
                    placeholder: '$100,000.00',
                    inputType: 'currencyFloat',
                    value: amount,
                    onChange: setAmount,
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'stage',
                    label: 'Stage',
                    placeholder: '',
                    inputType: 'select',
                    value: stage,
                    onChange: setStage,
                    options: [
                      {
                        value: 'Future Client (FIA)',
                        label: 'Future Client (FIA)',
                      },
                      { value: 'Cold', label: 'Cold' },
                      { value: 'Pitching', label: 'Pitching' },
                      { value: 'Onboarding', label: 'Onboarding' },
                      { value: 'Closed Lost', label: 'Closed Lost' },
                      { value: 'Unsuitable', label: 'Unsuitable' },
                      { value: 'Not Interested', label: 'Not Interested' },
                      { value: 'Client', label: 'Client' },
                      {
                        value: 'Already Has Financial Advisor',
                        label: 'Already Has Financial Advisor',
                      },
                    ],
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'closeDate',
                    label: 'Close Date',
                    placeholder: '',
                    inputType: 'date',
                    value: closeDate,
                    onChange: setCloseDate,
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'pitchType',
                    label: 'Pitch Type',
                    placeholder: '',
                    inputType: 'select',
                    value: pitchType,
                    onChange: setPitchType,
                    options: [
                      {
                        value: 'Conservative Income',
                        label: 'Conservative Income',
                      },
                      {
                        value: 'Aggressive Income',
                        label: 'Aggressive Income',
                      },
                      {
                        value: 'Conservative Accumulation',
                        label: 'Conservative Accumulation',
                      },
                      {
                        value: 'Aggressive Accumulation',
                        label: 'Aggressive Accumulation',
                      },
                    ],
                  })}
                </Grid>
                <Grid item xs={12}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'nextStep',
                    label: 'Next Step',
                    placeholder: '',
                    inputType: 'text',
                    value: nextStep,
                    onChange: setNextStep,
                  })}
                </Grid>
              </Grid>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                rel="noreferrer"
                variant="parcoRounded"
                color="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                rel="noreferrer"
                variant="parcoRounded"
                onClick={handleSubmit}
                endIcon={
                  isCreatingOpp && (
                    <CircularProgress size={20} color="inherit" />
                  )
                }
              >
                {isCreatingOpp ? 'Creating Opportunity' : buttonText}
              </Button>
            </Box>
          </Box>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}
